/**
 * Maps options from the input format to the legacy format.
 *
 * @param {Array} options - Array of options to be transformed.
 * @returns {Array} Transformed options in legacy format.
 */
function mapOptions(options) {
  return options.map(option => ({
    amount: option?.price?.finalPrice || 0,
    id: Number(option.id) || option.legacyId,
    name: option.name,
    requiresAgeCheck: option.requiresAgeCheck,
    hasStock: option.hasStock,
    ...(option?.maxQuantity > 0 && { maxQuantity: option.maxQuantity }),
  }));
}

/**
 * Maps option groups from the input format to the legacy format.
 *
 * @param {Array} optionGroups - Array of option groups to be transformed.
 * @returns {Array} Transformed option groups in legacy format.
 */
function mapOptionGroups(optionGroups) {
  if (!optionGroups || optionGroups.length === 0) {
    return [];
  }

  return optionGroups.map(group => ({
    id: Number(group.id) || group.legacyId,
    name: group.name,
    options: group.options ? mapOptions(group.options) : [],
    minQuantity: group?.quantity?.min ? group.quantity.min : 0,
    maxQuantity: group?.quantity?.max ? group.quantity.max : 0,
  }));
}

/**
 * Transforms an input product object to a legacy product format.
 *
 * @param {Object} inputProduct - The product object to be transformed.
 * @param {string} inputProduct.id - The product's current ID.
 * @param {number} inputProduct.legacyId - The product's legacy ID.
 * @param {string} inputProduct.description - Description of the product.
 * @param {Object} inputProduct.images - Object containing image URLs.
 * @param {string} inputProduct.name - Name of the product.
 * @param {Object} inputProduct.price - Object containing pricing info.
 * @param {Object} inputProduct.tags - Object containing various tags.
 * @returns {Object} Transformed product object in legacy format.
 */
export function mapperProductConfig(inputProduct) {
  const {
    id,
    legacyId,
    description,
    images = { urls: [] },
    name,
    price = {},
    tags = {},
  } = inputProduct;

  const outputProduct = {
    name,
    description,
    id: Number(id) || legacyId,
    image: images.urls[0] || '',
    optionGroups: mapOptionGroups(inputProduct.optionGroups),
    price: price.finalPrice || price.originalPrice,
    finalPriceWithoutTaxes: price.finalPriceWithoutTaxes || null,
    requiresAgeCheck: tags.requiresAgeCheck || false,
    isAvailable: tags.isAvailable || false,
    hideNotes: tags.hideNotes || false,
    isFavourite: tags.isFavourite || false,
    isMostOrdered: tags.isMostOrdered || false,
  };

  return outputProduct;
}
